import React, { useState, useEffect } from 'react';
import {
  Users,
  CheckCircle,
  XCircle,
  Send,
  Copy,
  RotateCw,
  Trash2,
  AlertCircle,
  Clock,
  Check,
  Shield,
  Sparkles,
  ExternalLink,
} from 'lucide-react';
import { invitationService } from '../lib/invitationService';
import {
  FirestoreInvitationRequestDoc,
  InvitationStatus,
  AllocatedTier,
  DeliveryStatus,
} from '../types/invitation';

interface InvitationManagementViewProps {
  lang?: string;
}

export const InvitationManagementView: React.FC<InvitationManagementViewProps> = ({
  lang = 'en',
}) => {
  const isHi = lang === 'hi';

  const [requests, setRequests] = useState<FirestoreInvitationRequestDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  // Approval modal state
  const [selectedReq, setSelectedReq] = useState<FirestoreInvitationRequestDoc | null>(null);
  const [selectedTier, setSelectedTier] = useState<AllocatedTier>('BASIC');
  const [actionLoading, setActionLoading] = useState(false);

  // Success message toast
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const fetchRequests = async () => {
    setLoading(true);
    setError(null);
    const filter = statusFilter === 'ALL' ? undefined : statusFilter;
    const res = await invitationService.getAdminRequests(filter);
    setLoading(false);

    if (res.success) {
      setRequests(res.requests);
    } else {
      setError(res.error || 'Failed to load requests');
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 4000);
  };

  const handleApprove = async (req: FirestoreInvitationRequestDoc) => {
    setActionLoading(true);
    const res = await invitationService.approveRequest(req.requestId, selectedTier);
    setActionLoading(false);
    setSelectedReq(null);

    if (res.success) {
      showToast(isHi ? 'अनुरोध स्वीकृत किया गया' : `Application approved for ${req.fullName}`);
      fetchRequests();
    } else {
      setError(res.error || 'Approval failed');
    }
  };

  const handleDecline = async (req: FirestoreInvitationRequestDoc) => {
    const reason = window.prompt(
      isHi ? 'अस्वीकृति का कारण (वैकल्पिक):' : 'Reason for declining (optional):'
    );
    if (reason === null) return;

    setActionLoading(true);
    const res = await invitationService.declineRequest(req.requestId, reason || undefined);
    setActionLoading(false);

    if (res.success) {
      showToast(isHi ? 'अनुरोध अस्वीकृत' : `Application declined for ${req.fullName}`);
      fetchRequests();
    } else {
      setError(res.error || 'Decline failed');
    }
  };

  const handleIssueAndCopy = async (req: FirestoreInvitationRequestDoc) => {
    setActionLoading(true);
    const res = await invitationService.issueInvitation(req.requestId, req.allocatedTier || 'BASIC');
    setActionLoading(false);

    if (res.success && res.invitationUrl) {
      await navigator.clipboard.writeText(res.invitationUrl);
      setCopiedId(req.requestId);
      showToast(isHi ? 'आमंत्रण लिंक कॉपी किया गया!' : 'Invitation link generated and copied to clipboard!');
      fetchRequests();
    } else {
      setError(res.error || 'Failed to issue invitation');
    }
  };

  const handleSendEmail = async (req: FirestoreInvitationRequestDoc) => {
    setActionLoading(true);
    const res = await invitationService.sendInvitationEmail(req.requestId);
    setActionLoading(false);

    if (res.success) {
      showToast(isHi ? 'आमंत्रण ईमेल प्रेषित!' : `Invitation email sent to ${req.email}!`);
      fetchRequests();
    } else {
      setError(res.error || 'Failed to dispatch email');
    }
  };

  const handleResendEmail = async (req: FirestoreInvitationRequestDoc) => {
    setActionLoading(true);
    const res = await invitationService.resendInvitationEmail(req.requestId);
    setActionLoading(false);

    if (res.success) {
      showToast(isHi ? 'आमंत्रण ईमेल पुनः प्रेषित!' : `Invitation email resent to ${req.email}!`);
      fetchRequests();
    } else {
      setError(res.error || 'Failed to resend email');
    }
  };

  const handleRevoke = async (req: FirestoreInvitationRequestDoc) => {
    if (!window.confirm(isHi ? 'क्या आप इस आमंत्रण को रद्द करना चाहते हैं?' : 'Are you sure you want to revoke this invitation?')) {
      return;
    }

    setActionLoading(true);
    const res = await invitationService.revokeInvitation(req.requestId);
    setActionLoading(false);

    if (res.success) {
      showToast(isHi ? 'आमंत्रण रद्द किया गया' : 'Invitation revoked');
      fetchRequests();
    } else {
      setError(res.error || 'Failed to revoke');
    }
  };

  const getStatusBadge = (status: InvitationStatus) => {
    switch (status) {
      case InvitationStatus.REQUESTED:
        return (
          <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-slate-800 text-slate-300 border border-slate-700">
            {isHi ? 'कोड लंबित' : 'Awaiting OTP'}
          </span>
        );
      case InvitationStatus.EMAIL_VERIFIED:
        return (
          <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-amber-500/10 text-amber-300 border border-amber-500/30">
            {isHi ? 'ईमेल सत्यापित' : 'Email Verified'}
          </span>
        );
      case InvitationStatus.APPROVED:
        return (
          <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-sky-500/10 text-sky-300 border border-sky-500/30">
            {isHi ? 'स्वीकृत' : 'Approved'}
          </span>
        );
      case InvitationStatus.INVITATION_ISSUED:
        return (
          <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-purple-500/10 text-purple-300 border border-purple-500/30">
            {isHi ? 'आमंत्रण जारी' : 'Issued'}
          </span>
        );
      case InvitationStatus.INVITATION_REDEEMED:
        return (
          <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
            {isHi ? 'खाता सृजित' : 'Redeemed'}
          </span>
        );
      case InvitationStatus.DECLINED:
        return (
          <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-red-500/10 text-red-300 border border-red-500/30">
            {isHi ? 'अस्वीकृत' : 'Declined'}
          </span>
        );
      case InvitationStatus.INVITATION_REVOKED:
        return (
          <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-rose-500/10 text-rose-300 border border-rose-500/30">
            {isHi ? 'रद्द' : 'Revoked'}
          </span>
        );
      default:
        return (
          <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-slate-800 text-slate-400">
            {status}
          </span>
        );
    }
  };

  const getDeliveryBadge = (deliveryStatus?: DeliveryStatus) => {
    if (!deliveryStatus) return null;
    switch (deliveryStatus) {
      case 'SENT':
        return (
          <span className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-950 text-emerald-300 border border-emerald-800">
            Email: Sent
          </span>
        );
      case 'DELIVERY_FAILED':
        return (
          <span className="px-1.5 py-0.5 rounded text-[10px] bg-red-950 text-red-300 border border-red-800">
            Email: Failed
          </span>
        );
      case 'QUEUED':
        return (
          <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-950 text-amber-300 border border-amber-800">
            Email: Queued
          </span>
        );
      default:
        return (
          <span className="px-1.5 py-0.5 rounded text-[10px] bg-slate-800 text-slate-400">
            {deliveryStatus}
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toastMessage && (
        <div className="p-3 bg-emerald-950/80 border border-emerald-800 rounded-xl flex items-center space-x-2 text-emerald-300 text-sm shadow-xl">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-950/80 border border-red-800 rounded-xl flex items-center space-x-2 text-red-300 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Control Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-slate-900 border border-slate-800 rounded-2xl">
        <div className="flex items-center space-x-3">
          <div className="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400">
            <Users className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base font-serif font-bold text-slate-100">
              {isHi ? 'आमंत्रण समीक्षा एवं प्रबंधन' : 'Invitation Review Queue'}
            </h2>
            <p className="text-xs text-slate-400">
              {isHi ? 'नियंत्रित पंजीकरण नीति (Option C)' : 'Governed Access Policy (VJ-PRODUCT-INVITATION-ONLY-001)'}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3 w-full sm:w-auto">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 bg-slate-950 border border-slate-700 rounded-lg text-xs text-slate-200 focus:outline-none focus:border-amber-400"
          >
            <option value="ALL">{isHi ? 'सभी आवेदन' : 'All Applications'}</option>
            <option value="EMAIL_VERIFIED">{isHi ? 'सत्यापित / समीक्षा लंबित' : 'Verified (Pending Review)'}</option>
            <option value="APPROVED">{isHi ? 'स्वीकृत' : 'Approved'}</option>
            <option value="INVITATION_ISSUED">{isHi ? 'जारी आमंत्रण' : 'Issued Tokens'}</option>
            <option value="INVITATION_REDEEMED">{isHi ? 'सृजित खाते' : 'Redeemed'}</option>
            <option value="DECLINED">{isHi ? 'अस्वीकृत' : 'Declined'}</option>
            <option value="INVITATION_REVOKED">{isHi ? 'रद्द' : 'Revoked'}</option>
          </select>

          <button
            onClick={fetchRequests}
            disabled={loading}
            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition disabled:opacity-50"
            title={isHi ? 'ताज़ा करें' : 'Refresh'}
          >
            <RotateCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Applications Table / Cards */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
        {loading && requests.length === 0 ? (
          <div className="py-12 text-center text-xs text-slate-400">
            {isHi ? 'आवेदनों को लोड किया जा रहा है...' : 'Loading invitation requests...'}
          </div>
        ) : requests.length === 0 ? (
          <div className="py-12 text-center text-xs text-slate-400">
            {isHi ? 'कोई आवेदन उपलब्ध नहीं है।' : 'No invitation applications found matching current filter.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-950/50 text-slate-400 uppercase font-mono tracking-wider text-[11px]">
                  <th className="py-3 px-4">Applicant</th>
                  <th className="py-3 px-4">Intent</th>
                  <th className="py-3 px-4">Statement / Referral</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Delivery</th>
                  <th className="py-3 px-4">Date</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60 text-slate-200">
                {requests.map((req) => (
                  <tr key={req.requestId} className="hover:bg-slate-800/40 transition">
                    <td className="py-3 px-4">
                      <div className="font-medium text-slate-100">{req.fullName}</div>
                      <div className="text-[11px] text-slate-400 font-mono">{req.email}</div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="capitalize px-2 py-0.5 rounded text-[11px] bg-slate-800 text-slate-300">
                        {req.intendedUsage}
                      </span>
                    </td>
                    <td className="py-3 px-4 max-w-xs truncate">
                      {req.reasonForJoining ? (
                        <div title={req.reasonForJoining} className="truncate text-slate-300">
                          {req.reasonForJoining}
                        </div>
                      ) : (
                        <span className="text-slate-500 italic">None</span>
                      )}
                      {req.referralCode && (
                        <div className="text-[10px] text-amber-400 font-mono">Ref: {req.referralCode}</div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {getStatusBadge(req.status)}
                    </td>
                    <td className="py-3 px-4">
                      {getDeliveryBadge(req.deliveryStatus)}
                    </td>
                    <td className="py-3 px-4 text-[11px] text-slate-400 whitespace-nowrap">
                      {new Date(req.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-right space-x-1 whitespace-nowrap">
                      {/* Review stage actions */}
                      {req.status === InvitationStatus.EMAIL_VERIFIED && (
                        <>
                          <button
                            onClick={() => setSelectedReq(req)}
                            disabled={actionLoading}
                            className="px-2.5 py-1 rounded bg-sky-600/20 hover:bg-sky-600/40 text-sky-300 border border-sky-500/30 transition text-[11px]"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleDecline(req)}
                            disabled={actionLoading}
                            className="px-2.5 py-1 rounded bg-red-600/20 hover:bg-red-600/40 text-red-300 border border-red-500/30 transition text-[11px]"
                          >
                            Decline
                          </button>
                        </>
                      )}

                      {/* Approved stage actions */}
                      {req.status === InvitationStatus.APPROVED && (
                        <>
                          <button
                            onClick={() => handleIssueAndCopy(req)}
                            disabled={actionLoading}
                            className="px-2 py-1 rounded bg-amber-600/20 hover:bg-amber-600/40 text-amber-300 border border-amber-500/30 transition text-[11px] inline-flex items-center space-x-1"
                            title="Generate token and copy link to clipboard"
                          >
                            <Copy className="w-3 h-3" />
                            <span>Issue & Copy</span>
                          </button>
                          <button
                            onClick={() => handleSendEmail(req)}
                            disabled={actionLoading}
                            className="px-2 py-1 rounded bg-purple-600/20 hover:bg-purple-600/40 text-purple-300 border border-purple-500/30 transition text-[11px] inline-flex items-center space-x-1"
                            title="Send email invitation directly to applicant"
                          >
                            <Send className="w-3 h-3" />
                            <span>Send Email</span>
                          </button>
                        </>
                      )}

                      {/* Issued stage actions */}
                      {req.status === InvitationStatus.INVITATION_ISSUED && (
                        <>
                          <button
                            onClick={() => handleSendEmail(req)}
                            disabled={actionLoading}
                            className="px-2 py-1 rounded bg-purple-600/20 hover:bg-purple-600/40 text-purple-300 border border-purple-500/30 transition text-[11px] inline-flex items-center space-x-1"
                            title="Resend email invitation"
                          >
                            <Send className="w-3 h-3" />
                            <span>Resend Email</span>
                          </button>
                          <button
                            onClick={() => handleRevoke(req)}
                            disabled={actionLoading}
                            className="px-2 py-1 rounded bg-rose-600/20 hover:bg-rose-600/40 text-rose-300 border border-rose-500/30 transition text-[11px] inline-flex items-center space-x-1"
                            title="Revoke active token"
                          >
                            <Trash2 className="w-3 h-3" />
                            <span>Revoke</span>
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Approval & Tier Allocation Modal */}
      {selectedReq && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-md bg-slate-900 border border-sky-500/30 rounded-2xl p-6 shadow-2xl text-slate-100 space-y-4">
            <h3 className="text-base font-serif font-bold text-sky-300">
              {isHi ? 'आमंत्रण अनुमोदन एवं स्तर आवंटन' : 'Approve Application & Allocate Tier'}
            </h3>
            <p className="text-xs text-slate-300">
              Granting access to <strong>{selectedReq.fullName}</strong> ({selectedReq.email}).
            </p>

            <div>
              <label className="block text-xs font-medium text-slate-300 mb-2">
                {isHi ? 'आवंटित खाता स्तर' : 'Provisioned Account Tier'}
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setSelectedTier('BASIC')}
                  className={`p-3 rounded-xl border text-left transition ${
                    selectedTier === 'BASIC'
                      ? 'bg-sky-500/20 border-sky-400 text-sky-200'
                      : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700'
                  }`}
                >
                  <div className="font-semibold text-xs">BASIC</div>
                  <div className="text-[10px] text-slate-400 mt-1">Core calculation tools, 10 saved charts</div>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedTier('PRO_ASTRO')}
                  className={`p-3 rounded-xl border text-left transition ${
                    selectedTier === 'PRO_ASTRO'
                      ? 'bg-sky-500/20 border-sky-400 text-sky-200'
                      : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700'
                  }`}
                >
                  <div className="font-semibold text-xs">PRO ASTRO</div>
                  <div className="text-[10px] text-slate-400 mt-1">Unlimited charts, advanced modules & exports</div>
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-3">
              <button
                type="button"
                onClick={() => setSelectedReq(null)}
                className="px-4 py-2 rounded-xl text-xs font-medium bg-slate-800 text-slate-300 hover:bg-slate-700 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleApprove(selectedReq)}
                disabled={actionLoading}
                className="px-4 py-2 rounded-xl text-xs font-semibold bg-sky-600 hover:bg-sky-500 text-slate-950 transition flex items-center space-x-1.5 disabled:opacity-50"
              >
                <span>Confirm & Approve</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

